import { Handle, Position } from '@xyflow/react';
import { useTranslation } from 'react-i18next';
import { Building2, Users, Minus } from 'lucide-react';
import { getPaletteColor } from '@/core/lib/colors';
import { useTreeCollapse } from '@/shared/components/custom/tree-chart';
import type { OrgProjectNodeData } from '../../hooks/useOrgChartProjectsData';

interface OrgProjectNodeProps {
  data: OrgProjectNodeData;
}

const STATUS_COLORS: Record<string, string> = {
  active: '#16a34a',
  paused: '#d97706',
  completed: '#6b7280',
  archived: '#6b7280',
};

const DEFAULT_STATUS_COLOR = '#94a3b8';
const DEFAULT_BAR_COLOR = '#1065ca';

function resolveProjectColor(color: string | null): string {
  if (!color) return DEFAULT_BAR_COLOR;
  return getPaletteColor(color)?.value ?? color;
}

export function OrgProjectNode({ data }: OrgProjectNodeProps) {
  const { t } = useTranslation('organizationUnits');
  const { projectId, name, status, color, areaCount, memberCount, unassignedCount, hasChildren } =
    data;
  const { collapsedIds, toggleCollapse } = useTreeCollapse();
  const collapsed = collapsedIds.has(`project-${projectId}`);

  const barColor = resolveProjectColor(color);
  const statusKey = (status ?? '').toLowerCase();
  const statusColor = STATUS_COLORS[statusKey] ?? DEFAULT_STATUS_COLOR;
  const statusLabel = STATUS_COLORS[statusKey]
    ? t(`orgChart.projectNode.status.${statusKey}`)
    : status;

  const directChildCount = areaCount + unassignedCount;

  return (
    <div
      className="group relative flex w-55 flex-col overflow-hidden rounded-xl border bg-card shadow-sm transition-shadow hover:shadow-md"
      style={{ pointerEvents: 'all' }}
      data-testid={`org-chart-project-node-${projectId}`}
    >
      <Handle type="target" position={Position.Top} className="border-border! bg-muted!" />

      <div className="h-1" style={{ backgroundColor: barColor }} />

      <div className="flex flex-col gap-2 px-3 py-3">
        <div className="flex items-start justify-between gap-2">
          <p className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">{name}</p>
          {statusLabel && (
            <div className="flex shrink-0 items-center gap-1">
              <span
                className="size-1.5 rounded-full"
                style={{ backgroundColor: statusColor }}
                aria-hidden
              />
              <span className="text-[10px] font-medium" style={{ color: statusColor }}>
                {statusLabel}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Building2 className="h-3 w-3" />
            <span>{t('orgChart.projectNode.areas', { count: areaCount })}</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Users className="h-3 w-3" />
            <span>{t('orgChart.projectNode.members', { count: memberCount })}</span>
          </div>
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        style={
          hasChildren
            ? { opacity: 0, pointerEvents: 'none' }
            : { backgroundColor: barColor, borderColor: barColor }
        }
      />

      {hasChildren && (
        <button
          type="button"
          className="absolute bottom-0 left-1/2 z-20 flex h-5 w-5 -translate-x-1/2 translate-y-1/2 items-center justify-center rounded-full text-white shadow-sm"
          style={{ backgroundColor: barColor }}
          onClick={() => toggleCollapse(`project-${projectId}`)}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          data-testid={`org-chart-project-collapse-${projectId}`}
        >
          {collapsed ? (
            <span className="text-[10px] font-bold leading-none">{directChildCount}</span>
          ) : (
            <Minus className="h-3 w-3" />
          )}
        </button>
      )}
    </div>
  );
}
